import { Check, Minus } from 'lucide-react';
import { Checkbox as CheckboxPrimitive, Switch as SwitchPrimitive } from 'radix-ui';
import * as React from 'react';
import { cn } from '../../lib/cn.js';

export interface CheckboxProps extends React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root> {
  label?: React.ReactNode;
  description?: React.ReactNode;
}

export const Checkbox = React.forwardRef<HTMLButtonElement, CheckboxProps>(function Checkbox(
  { className, label, description, id, ...props },
  ref,
) {
  const autoId = React.useId();
  const cid = id ?? autoId;
  const box = (
    <CheckboxPrimitive.Root
      ref={ref}
      id={cid}
      className={cn(
        'peer inline-flex size-5 shrink-0 items-center justify-center rounded-[6px] border border-border-control-strong bg-surface-default',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus',
        'data-[state=checked]:border-brand-primary data-[state=checked]:bg-brand-primary data-[state=checked]:text-brand-on-primary',
        'data-[state=indeterminate]:border-brand-primary data-[state=indeterminate]:bg-brand-primary data-[state=indeterminate]:text-brand-on-primary',
        'disabled:cursor-not-allowed disabled:opacity-50 aria-invalid:border-danger',
        className,
      )}
      {...props}
    >
      <CheckboxPrimitive.Indicator>
        {props.checked === 'indeterminate' ? <Minus className="size-3.5" aria-hidden /> : <Check className="size-3.5" aria-hidden />}
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  );
  if (!label) return box;
  return (
    <div className="flex items-start gap-3">
      <div className="pt-0.5">{box}</div>
      <div className="grid gap-0.5">
        <label htmlFor={cid} className="text-body font-medium text-text-primary peer-disabled:opacity-50">
          {label}
        </label>
        {description ? <p className="text-helper text-text-secondary">{description}</p> : null}
      </div>
    </div>
  );
});

export interface SwitchProps extends React.ComponentPropsWithoutRef<typeof SwitchPrimitive.Root> {
  label?: React.ReactNode;
  description?: React.ReactNode;
}

export const Switch = React.forwardRef<HTMLButtonElement, SwitchProps>(function Switch(
  { className, label, description, id, ...props },
  ref,
) {
  const autoId = React.useId();
  const sid = id ?? autoId;
  const control = (
    <SwitchPrimitive.Root
      ref={ref}
      id={sid}
      className={cn(
        'inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-pill border border-border-control-strong p-0.5 transition-colors duration-[var(--cm-motion-fast)]',
        'bg-surface-subtle data-[state=checked]:border-brand-primary data-[state=checked]:bg-brand-primary',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus disabled:cursor-not-allowed disabled:opacity-50',
        className,
      )}
      {...props}
    >
      <SwitchPrimitive.Thumb className="block size-[18px] rounded-pill bg-border-control-strong shadow-card transition-transform duration-[var(--cm-motion-fast)] data-[state=checked]:translate-x-5 data-[state=checked]:bg-surface-default" />
    </SwitchPrimitive.Root>
  );
  if (!label) return control;
  return (
    <div className="flex items-start justify-between gap-4">
      <div className="grid gap-0.5">
        <label htmlFor={sid} className="text-body font-medium text-text-primary">
          {label}
        </label>
        {description ? <p className="text-helper text-text-secondary">{description}</p> : null}
      </div>
      {control}
    </div>
  );
});
